import React, { useState } from 'react';
import SEOMetaTags from '../components/SEOMetaTags';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';
import { whatsappLink, PHONE_NUMBER, PHONE_DISPLAY, submitLead } from '../utils/contact';

const Contact = ({ service }) => {
    const { t } = useLanguage();
    usePageTitle('contact');

    const copy = (key) => (service ? t(`contactPage.${service}.${key}`) : t(`contactPage.${key}`));
    const whatsappIntro = service ? t(`contact.whatsapp.${service}`) : t('contact.whatsapp.generic');

    const [formSubmitted, setFormSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        Name: '',
        Phone: '',
        Email: '',
        Location: '',
        Message: ''
    });
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const objections = [
        { q: copy('objections.q1'), a: copy('objections.a1') },
        { q: copy('objections.q2'), a: copy('objections.a2') },
        { q: copy('objections.q3'), a: copy('objections.a3') },
        { q: copy('objections.q4'), a: copy('objections.a4') },
        { q: copy('objections.q5'), a: copy('objections.a5') }
    ];

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const subjectKey = service === 'agency' ? 'agency' : service === 'studio' ? 'studio' : 'generic';
        try {
            await submitLead({
                subject: t(`contact.form_subject.${subjectKey}`),
                fields: {
                    name: formData.Name,
                    phone: formData.Phone,
                    email: formData.Email,
                    location: formData.Location,
                    message: formData.Message,
                    url: window.location.pathname
                }
            });
        } catch (err) {
            console.error('[Contact] No se pudo enviar la consulta:', err);
        } finally {
            setSubmitting(false);
            setFormSubmitted(true);
        }
    };

    const getWhatsAppMessage = () => {
        const lines = [
            whatsappIntro,
            `${t('contact.form_labels.name')}: ${formData.Name}`,
            `${t('contact.form_labels.phone')}: ${formData.Phone}`
        ];
        if (formData.Email) lines.push(`${t('contact.form_labels.email')}: ${formData.Email}`);
        if (formData.Location) lines.push(`${t('contact.form_labels.location')}: ${formData.Location}`);
        if (formData.Message) lines.push(`${t('contact.form_labels.message')}: ${formData.Message}`);
        return lines.join('\n');
    };

    return (
        <div className="inner-page-content" data-page="contact">
            <SEOMetaTags pageKey="contact" />

            {/* Hero */}
            <div className="mxd-section mxd-section-inner-headline padding-text-pre-block mb-4 mb-md-5">
                <div className="mxd-container grid-container">
                    <div className="mxd-block loading-wrap">
                        <div className="container-fluid px-0">
                            <div className="row gx-0 align-items-center">
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                    <div className="mxd-block__name name-inner-headline loading__item">
                                        <p className="mxd-point-subtitle">
                                            <i className="ph-bold ph-phone-call me-2"></i>
                                            <span>{copy('badge')}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-10 mxd-grid-item no-margin">
                                    <div className="mxd-block__content">
                                        <div className="mxd-block__inner-headline">
                                            <h1 className="inner-headline__title h1-small">
                                                <b>{copy('title')}</b>
                                            </h1>
                                            <p className="t-large t-bright mt-4">
                                                {copy('subtitle')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sección de objeciones: dudas más comunes resueltas */}
            <div className="mxd-section padding-pre-grid my-4 my-md-5 py-3 py-md-4">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title">
                            <div className="container-fluid p-0">
                                <div className="row g-0">
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrtitle">
                                            <h2 className="reveal-type">{copy('objections.title')}</h2>
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrdescr">
                                            <p className="anim-uni-in-up">{copy('objections.subtitle')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin"></div>
                                <div className="col-12 col-xl-8 mxd-grid-item no-margin">
                                    <div className="mxd-accordion loading__fade">
                                        {objections.map((item, index) => {
                                            const isOpen = openFaq === index;
                                            return (
                                                <div key={index} className={`mxd-accordion__item ${isOpen ? 'active' : ''}`}>
                                                    <div className="mxd-accordion__divider anim-uni-in-up"></div>
                                                    <div
                                                        className="mxd-accordion__title anim-uni-in-up"
                                                        onClick={() => toggleFaq(index)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <h6>{item.q}</h6>
                                                        <div className={`mxd-accordion__arrow ${isOpen ? 'accordion-rotate' : ''}`}>
                                                            <i className="ph ph-plus"></i>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="mxd-accordion__content py-3"
                                                        style={{
                                                            display: isOpen ? 'flex' : 'none',
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start'
                                                        }}
                                                    >
                                                        <p className="mxd-accordion__text t-large text-secondary mb-3">{item.a}</p>
                                                    </div>
                                                    <div className="mxd-accordion__divider anim-uni-in-up"></div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Formulario de contacto */}
            <div className="mxd-section mxd-section-inner-form padding-default">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                    <div className="mxd-block__name name-inner-headline">
                                        <p className="mxd-point-subtitle">
                                            <i className="ph-bold ph-envelope me-2"></i>
                                            <span>{copy('form_title')}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-8 mxd-grid-item no-margin">
                                    <div className="mxd-block__content contact">
                                        <div className="mxd-block__inner-form loading__fade">
                                            <div className="form-container">

                                                {/* Acciones de Contacto: Móvil (Llamar + WhatsApp) */}
                                                <div className="d-flex d-md-none flex-column gap-3 mb-5">
                                                    <a
                                                        href={`tel:${PHONE_NUMBER}`}
                                                        className="btn btn-anim btn-primary btn-large slide-right-up w-100"
                                                    >
                                                        <span className="btn-caption">{t('contactPage.call_btn')}</span>
                                                        <i className="ph-bold ph-phone-call"></i>
                                                    </a>
                                                    <a
                                                        href={whatsappLink(getWhatsAppMessage())}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-anim btn-outline btn-large slide-right-up w-100"
                                                        style={{ borderColor: '#25d366', color: '#25d366' }}
                                                    >
                                                        <span className="btn-caption">{t('contactPage.whatsapp_btn')}</span>
                                                        <i className="ph-bold ph-whatsapp-logo"></i>
                                                    </a>
                                                </div>

                                                <h3 className="h2 mb-4">{copy('form_title')}</h3>
                                                <p className="t-large text-secondary mb-4">{copy('form_description')}</p>

                                                {formSubmitted ? (
                                                    <div className="text-center py-4">
                                                        <i className="ph-fill ph-smiley-wink fs-1 mb-3 text-tertiary d-block"></i>
                                                        <p className="h3 mb-2">{t('contactPage.reply_title')}</p>
                                                        <span className="t-large text-secondary">
                                                            {t('contactPage.reply_text')}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <form className="form contact-form" id="contact-form" onSubmit={handleSubmit}>
                                                        <div className="container-fluid p-0">
                                                            <div className="row gx-0">
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="text"
                                                                        name="Name"
                                                                        placeholder={t('contactPage.name_placeholder')}
                                                                        required
                                                                        value={formData.Name}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="tel"
                                                                        name="Phone"
                                                                        placeholder={t('contactPage.phone_placeholder')}
                                                                        required
                                                                        value={formData.Phone}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="email"
                                                                        name="Email"
                                                                        placeholder={t('contactPage.email_placeholder')}
                                                                        value={formData.Email}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="text"
                                                                        name="Location"
                                                                        placeholder={t('contactPage.location_placeholder')}
                                                                        value={formData.Location}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                                <div className="col-12 mxd-grid-item anim-uni-in-up">
                                                                    <textarea
                                                                        name="Message"
                                                                        placeholder={copy('message_placeholder')}
                                                                        value={formData.Message}
                                                                        onChange={handleChange}
                                                                    ></textarea>
                                                                </div>

                                                                <div className="col-12 mxd-grid-item anim-uni-in-up mt-4">
                                                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                                                        <button
                                                                            className="btn btn-anim btn-default btn-large btn-opposite slide-right-up"
                                                                            type="submit"
                                                                            disabled={submitting}
                                                                        >
                                                                            <span className="btn-caption">{t('contactPage.submit_btn')}</span>
                                                                            <i className="ph-bold ph-arrow-up-right"></i>
                                                                        </button>

                                                                        {/* Desktop: solo se muestra el teléfono, sin link de WhatsApp */}
                                                                        <div className="d-none d-md-flex align-items-center ms-auto">
                                                                            <span className="t-large t-bright">
                                                                                <i className="ph-bold ph-phone-call me-2"></i>
                                                                                {PHONE_DISPLAY}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </form>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
